import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { GoogleMapsService } from "../../../../../../core/services/google-maps.service";
import { Community } from "../../../../../../core/models/community.model";
import { ApiError } from "../../../../../../core/models/api-error.model";
import { AdminCommunityService } from "../../../../../../core/services/admin/admin-community.service";
import { CommunityService } from "../../../../../../core/services/connected/community.service";

@Component({
  selector: "admin-onboarding-address",
  templateUrl: "./admin-onboarding-address.page.html",
  styleUrls: ["./admin-onboarding-address.page.scss"]
})
export class AdminOnboardingAddressPage implements OnInit, AfterViewInit {

  @ViewChild("installationAddress", { static: false }) installationAddressElementRef!: ElementRef;
  @ViewChild("billingAddress", { static: false }) billingAddressElementRef!: ElementRef;

  readonly #googleMapsService = inject(GoogleMapsService);
  readonly #adminCommunityService = inject(AdminCommunityService);
  readonly #communityService = inject(CommunityService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  private readonly _addressesForm: FormGroup;
  private _isLoading: boolean = false;
  private _useSameAddress: boolean = true;

  readonly #profileData: any;
  readonly #imageData: File | null = null;
  private _error: ApiError | null = null;

  readonly #communityId: string = this.#activatedRoute.snapshot.params["communityId"];
  #communityToUpdate: Community | null = null;

  constructor() {

    const routeState = this.#router.getCurrentNavigation()?.extras.state;

    if (!routeState || !routeState["profile"]) {
      if (!this.#communityId) {
        this.#router.navigate(["/admin/onboarding/profile"]);
      }
    } else {
      this.#profileData = routeState["profile"];
      this.#imageData = routeState["profilePicture"];
    }

    this._addressesForm = new FormGroup({
      installationAddress: new FormGroup({
        street: new FormControl("", [Validators.required]),
        zipCode: new FormControl("", [Validators.required, Validators.pattern("^\\d{5}$")]),
        city: new FormControl("", [Validators.required]),
        country: new FormControl("", [Validators.required, Validators.pattern(/^france$/i)]),
        infos: new FormControl("")
      }),
      billingAddress: new FormGroup({
        street: new FormControl(""),
        zipCode: new FormControl(""),
        city: new FormControl(""),
        country: new FormControl(""),
        infos: new FormControl("")
      })
    });
  }

  ngOnInit() {
    if (this.#communityId) {
      this.#communityService.getCommunityById(this.#communityId).subscribe((community) => {
        this.#communityToUpdate = community;
        this._addressesForm?.get("installationAddress")?.patchValue({
          street: community.addressInstallation.street,
          zipCode: community.addressInstallation.zipCode,
          city: community.addressInstallation.street,
          country: "France",
          infos: community.addressInstallation.infos
        });
      });
    }
  }

  ngAfterViewInit(): void {
    this.#googleMapsService.initAutocomplete(this.installationAddressElementRef.nativeElement, this._addressesForm.get("installationAddress") as FormGroup);
    this.#googleMapsService.initAutocomplete(this.billingAddressElementRef.nativeElement, this._addressesForm.get("billingAddress") as FormGroup);
  }


  get addressesForm(): FormGroup {
    return this._addressesForm;
  }

  get error(): ApiError | null {
    return this._error;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get useSameAddress(): boolean {
    return this._useSameAddress;
  }

  toggleAddress(): void {
    this._useSameAddress = !this._useSameAddress;
    const billingAddressForm = this._addressesForm.get("billingAddress") as FormGroup;
    if (this._useSameAddress) {
      billingAddressForm.reset();
      this.clearFormValidators(billingAddressForm);
    } else {
      this.updateFormValidators(billingAddressForm);
    }
  }

  private updateFormValidators(form: FormGroup) {
    form.get("street")?.setValidators([Validators.required]);
    form.get("street")?.updateValueAndValidity();
    form.get("zipCode")?.setValidators([Validators.required]);
    form.get("zipCode")?.updateValueAndValidity();
    form.get("city")?.setValidators([Validators.required]);
    form.get("city")?.updateValueAndValidity();
    form.get("country")?.setValidators([Validators.required, Validators.pattern(/^france$/i)]);
    form.get("country")?.updateValueAndValidity();
  }

  private clearFormValidators(form: FormGroup) {
    form.get("street")?.setValidators(null);
    form.get("street")?.updateValueAndValidity();
    form.get("zipCode")?.setValidators(null);
    form.get("zipCode")?.updateValueAndValidity();
    form.get("city")?.setValidators(null);
    form.get("city")?.updateValueAndValidity();
    form.get("country")?.setValidators(null);
    form.get("country")?.updateValueAndValidity();
  }

  previousStep() {
    if (this.#communityId) {
      this.#router.navigate(["/admin/onboarding/" + this.#communityId + "/profile"]);
    } else {
      this.#router.navigate(["/admin/onboarding/profile"], {
        state: {
          profile: this.#profileData,
          profilePicture: this.#imageData
        }
      });
    }
  }

  createAddresses() {

    if (this.addressesForm.valid) {
      this._isLoading = true;

      let data = {
        ...this.#profileData,
        ...this.addressesForm.value.installationAddress
      };

      if (!this.useSameAddress) {
        data = {
          ...data,
          billingAddress: this.addressesForm.value.billingAddress
        };
      }

      if (this.#communityId) {
        if (!this.noChanges()) {
          this.#communityService.updateCommunity(this.#communityId, data, this.#imageData).subscribe({
            next: (community) => {
              this.#router.navigate(["/admin/onboarding", community.id, "subscription"]);
              this._isLoading = false;
            },
            error: (error) => {
              this._error = error.error;
              this._isLoading = false;
            }
          });
        } else {
          this.#router.navigate(["/admin/onboarding", this.#communityId, "subscription"]);
        }
      } else {
        this.#adminCommunityService.createCommunity(data, this.#imageData).subscribe({
          next: (community) => {
            this.#router.navigate(["/admin/onboarding", community.id, "subscription"]);
            this._isLoading = false;
          },
          error: (error) => {
            this._error = error.error;
            this._isLoading = false;
          }
        });
      }
    }

  }

  public noChanges() {
    return JSON.stringify(this.#communityToUpdate?.relative) === JSON.stringify(this.#profileData)
      || JSON.stringify(this.#communityToUpdate?.addressInstallation) === JSON.stringify(this.addressesForm.value.installationAddress);
  }

}
